import React, { useState } from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { Link, useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Envelope, Eye, EyeClosed, Lock } from '@phosphor-icons/react'
import { Formik } from 'formik'

import { getRoleLandingPath } from '@/utils/auth'

import TextField from '../FormLegacy/FormFieldsV2/TextField'
import { loginUser, selectIsAuth, setUserData } from '../../reducers/userDataReducer'
import api from '../../api/api'
import { isMobileDevice, removeAuthCookie } from '../../helpers/helpers'
import { formValidation } from '../FormLegacy/validation'

import { getAuthRedirectUrl } from './utils'

const Login = ({ setStep, setUserData, loginUser, email }) => {
  const { t } = useTranslation(['auth', 'common'])
  const [error, setError] = useState(null)
  const [showPassword, setShowPassword] = React.useState(false)

  const navigate = useNavigate()

  const onSubmit = (values) => {
    setError(null)

    api
      .getUserToken(values.email, values.password, true)
      .then(api.getUserData)
      .then((userData) => {
        if (userData.user.role === 'ParkingStaff') {
          setError(t('auth:login.parkingStaffNotAllowed'))
          return
        }

        if (userData.user.role === 'Valet') {
          setError('The Valet user is not allowed to enter the Web portal.')
          return
        }

        if (isMobileDevice()) {
          navigate('/auth/success')
          return
        }
        setUserData(userData)
        loginUser()
        redirectDependsOnRole(userData)
      })
      .catch((err) => {
        console.error(err)
        handleServerResponse(err)
      })
  }

  const handleServerResponse = (err) => {
    const { status, response } = err || {}

    let res = {}
    if (response) {
      try {
        res = JSON.parse(response)
      } catch {
        res = {}
      }
    }

    if (status === 404) {
      setError(t('auth:login.userNotFound'))
    } else if (status === 401) {
      setError(res.message || t('auth:login.serverError'))
    } else if (status === 403) {
      removeAuthCookie('token')
      removeAuthCookie('role')
      if (Object.prototype.hasOwnProperty.call(res, 'data') && res.data.isBlocked) {
        setError(<div>{res.message}</div>)
      } else {
        setError(
          <div>
            {res.message}
            <Link to="/resend-activation-link">{t('auth:login.generateActivationLink')}</Link>
          </div>,
        )
      }
    } else {
      setError(t('auth:login.serverError'))
    }
  }

  const redirectDependsOnRole = (userData) => {
    window.location.href = getAuthRedirectUrl(getRoleLandingPath(userData))
  }

  const validationSchema = Yup.object().shape({
    email: formValidation.email,
    password: formValidation.password,
  })

  return (
    <Formik
      initialValues={{
        email,
        password: '',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ handleSubmit, values, handleChange, touched, errors, setFieldTouched }) => (
        <form onSubmit={handleSubmit} className="auth__form">
          <button
            type="button"
            className="auth__back-button"
            onClick={() => setStep('email-checkup')}
          >
            <ArrowLeft />
            {t('common:actions.back')}
          </button>
          <TextField
            type="email"
            name="email"
            label={t('auth:login.title')}
            placeholder={t('auth:shared.email')}
            value={values.email}
            leftIcon={<Envelope />}
            // Note: You can't edit email in this step, it's email passed on previous page
            disabled
            onChange={handleChange}
            isError={touched.email && !!errors.email}
          />
          <TextField
            type={showPassword ? 'text' : 'password'}
            name="password"
            label={t('auth:shared.password')}
            placeholder={t('auth:shared.password')}
            value={values.password}
            leftIcon={<Lock />}
            onChange={handleChange}
            isError={(touched.password && !!errors.password) || !!error}
            onBlur={() => setFieldTouched('password')}
            rightIcon={
              <div className="password-eye" onClick={() => setShowPassword((prev) => !prev)}>
                {showPassword ? <EyeClosed /> : <Eye />}
              </div>
            }
          />
          {error && <p className="auth__form__description error">{error}</p>}
          <Link className="auth__forgot-password" to="/change-password">
            {t('auth:login.forgotPassword')}
          </Link>
          <button type="submit" className="button button-primary">
            {t('auth:login.signIn')}
          </button>
        </form>
      )}
    </Formik>
  )
}

Login.propTypes = {
  auth: PropTypes.bool,
  email: PropTypes.string,
  setStep: PropTypes.func,
  setUserData: PropTypes.func,
  loginUser: PropTypes.func,
}

export default connect((state) => ({ auth: selectIsAuth(state) }), {
  setUserData,
  loginUser,
})(Login)
